import AssistantComposer, { AssistantAttachments } from './AssistantComposer';
import { useAssistantDraft } from '../lib/assistantDraft';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skull, Send, X, Maximize2, Minimize2, Loader2, ThumbsUp, ThumbsDown, Plus,
  MessageSquare, ChevronLeft } from 'lucide-react';
import { assistantApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { subscribeFabStack, reservedHeight } from '../lib/fabStack';
import RichText from './RichText';
import ProposalCard from './ProposalCard';
import CopyProposalCard from './CopyProposalCard';
import AssistantPage from '../pages/AssistantPage';

/*
 * The Undead assistant, as a support bubble in the bottom-right corner of every page.
 *
 * It used to be a sidebar entry, which meant leaving whatever you were doing to ask a question
 * about it. A support chat belongs where the question occurs.
 *
 * Two things this deliberately does NOT do:
 *
 *   - It does not render at all unless /assistant/info says enabled. The flag is off by default
 *     and model calls cost money, so a bubble that opens and then apologises is worse than no
 *     bubble. `retry: false` for the same reason - on a server without the endpoint this must
 *     fail once and stay quiet, not poll.
 *   - It does not own the corner. The slot settings Save button is contextual and urgent; this
 *     is ambient. So Save keeps the corner and the bubble lifts above it, via lib/fabStack.
 *
 * The full page at /assistant is still the home for conversation history and ratings; this is
 * the quick lane, with an expand button that hands over to it.
 */
/*
 * "Did this resolve it?" - the entry point to the A6 feedback loop.
 *
 * A "No" is not a dead end: it raises a knowledge-base gap ticket, a human writes the missing
 * answer once, and it is then delivered into this conversation and served to everyone who asks
 * the same thing afterwards. Which is why this belongs in the bubble and not only on the full
 * page - the bubble is now where nearly every question gets asked, so leaving it out would
 * quietly starve the loop of the signal it runs on.
 *
 * Stacked rather than inline: the panel is 380px, and the page's single-row version wraps into a
 * mess at that width.
 */
/**
 * Server messages -> transcript rows. ONE mapping, used by every path that fills the transcript.
 *
 * It exists because there used to be two. The restore-after-refresh path had its own copy that
 * kept only role and content, so every message came back WITHOUT an id - and the rating controls
 * render on `m.id`. The result was that reloading the page silently removed 'Did this resolve it?'
 * from every answer, which starves the A6 feedback loop: a 'No' is what raises a KB-gap ticket.
 * Two mappings of the same shape is the bug; this is the fix.
 */
const toTranscript = (rows) => (rows || []).map((m) => ({
  id: m.id,
  role: m.fromSupport ? 'assistant' : m.role,
  content: m.content, images: m.images || [],
  fromSupport: m.fromSupport === true,
  resolved: m.resolved,
  rating: m.rating,
}));

function RatingRow({ message, onRate, busy }) {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState('');
  const [comment, setComment] = useState('');

  if (message.resolved === true) {
    return <div className="mt-1 pl-1 text-[11px] text-success">Marked as resolved — thanks.</div>;
  }
  if (message.resolved === false && !open) {
    return (
      <div className="mt-1 pl-1 text-[11px] text-muted-foreground">
        Sent to support — a human will reply right here.
      </div>
    );
  }
  if (!open) {
    return (
      <div className="mt-1 flex items-center gap-1 pl-1 text-[11px] text-muted-foreground">
        Did this resolve it?
        <Button size="sm" variant="ghost" disabled={busy}
          className="h-6 px-1.5 text-[11px] text-success hover:text-success"
          onClick={() => onRate(message.id, { resolved: true })}>
          <ThumbsUp className="mr-1 h-3 w-3" /> Yes
        </Button>
        <Button size="sm" variant="ghost" disabled={busy}
          className="h-6 px-1.5 text-[11px] text-warning hover:text-warning"
          onClick={() => setOpen(true)}>
          <ThumbsDown className="mr-1 h-3 w-3" /> No
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-1.5 w-full space-y-1.5 pl-1">
      <select value={score} onChange={(e) => setScore(e.target.value)} aria-label="Score"
        className="h-7 w-full rounded-md border border-border bg-secondary/60 px-1.5 text-[11px] text-foreground">
        <option value="">score (optional)</option>
        {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500}
        placeholder="what was missing? (optional)"
        className="h-7 w-full rounded-md border border-border bg-secondary/60 px-2 text-[11px] text-foreground outline-none focus:ring-2 focus:ring-ring" />
      <div className="flex gap-1.5">
        <Button size="sm" disabled={busy}
          className="h-7 flex-1 bg-warning text-[11px] text-background hover:bg-warning/90"
          onClick={() => {
            onRate(message.id, {
              resolved: false,
              rating: score || undefined,
              comment: comment || undefined,
            });
            setOpen(false);
          }}>
          Send to support
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setOpen(false)}>
          cancel
        </Button>
      </div>
    </div>
  );
}

export default function AssistantBubble() {
  const [open, setOpen] = useState(false);

  const [chatId, setChatId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  /*
   * expanded: grow the overlay to fill the viewport IN PLACE. It deliberately does not
   * navigate to /assistant - the whole point of a bubble is that you keep the page you are
   * on. Someone half-way through editing slot settings must be able to ask a question and
   * come back to their unsaved edits, and a route change throws those away.
   */
  const [expanded, setExpanded] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  /* Kept beside the transcript rather than on a message: the reload that follows every send
   * replaces the transcript wholesale, and a proposal stored on a row would be lost with it.
   * It is transient by nature anyway - accept it, decline it, or ask again. */
  const [proposal, setProposal] = useState(null);
  /* A slot-to-slot copy plan, kept beside `proposal` rather than folded into it: the two come
   * from different server contracts (this one's diff rows are old/new, not oldValue/newValue) and
   * are applied through different endpoints. The server sends at most one of them per reply, so
   * they never compete for the space below the transcript. Same transient lifetime as `proposal`
   * for the same reason - it dies with the conversation it was proposed in. */
  const [copy, setCopy] = useState(null);
  const [lift, setLift] = useState(reservedHeight());
  const scrollRef = useRef(null);
  const minimizeRef = useRef(null);
  const inputRef = useRef(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [draft, setDraft, images, setImages] = useAssistantDraft(user?.email);

  /*
   * The SAME key the full /assistant page uses, deliberately - the bubble and the page are two
   * windows onto one conversation, so expanding from here must land on what you were just
   * reading, and a reply given in one must be visible in the other.
   *
   * Without this restore the bubble looked empty after every refresh AND silently opened a NEW
   * server-side chat on the next message, orphaning the old one. The page hit exactly this and
   * fixed it (AssistantPage: "without this, every visit looked like the history was gone and
   * silently started a new server chat"); this component was written without it and reintroduced
   * the same defect.
   */
  const storeKey = user?.email ? `assistantChatId:${user.email}` : null;

  // Whatever else is pinned to this corner (the Save button) decides how high we sit.
  useEffect(() => subscribeFabStack(setLift), []);

  /*
   * A shared browser must not accumulate a list of everyone who has ever signed in on it.
   *
   * The conversation key is namespaced by email so one account cannot reopen another's chat, and
   * that namespacing is exactly what leaves an address behind: logging out clears the tokens but
   * not this. The signed-in account sweeps every OTHER account's key, from the one component that
   * is mounted on every authenticated page (Layout renders this bubble), so the page's copy of
   * the same key is covered too. Nothing of value is lost - the conversations themselves live
   * server-side and are listed by the Conversations button.
   *
   * This does not cover the last account to sign out on a browser nobody signs into again; that
   * needs AuthContext.logout, which is not this component's to change.
   */
  useEffect(() => {
    if (!storeKey) return;
    try {
      const stale = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.indexOf('assistantChatId:') === 0 && k !== storeKey) stale.push(k);
      }
      stale.forEach((k) => localStorage.removeItem(k));
      if (stale.length) {
        console.info(`[assistant] cleared ${stale.length} saved conversation key(s) belonging to`
          + ' other accounts on this browser');
      }
    } catch (_) { /* private mode, or storage disabled - nothing to clear either way */ }
  }, [storeKey]);

  const infoQuery = useQuery({
    queryKey: ['assistant-info'],
    queryFn: () => assistantApi.info(),
    retry: false,
    staleTime: 60000,
  });
  const enabled = infoQuery.data?.data?.enabled === true;
  const ratingEnabled = infoQuery.data?.data?.ratingEnabled === true;

  /*
   * Rating needs the server's message ids, and the optimistic splice below has none - so after a
   * send we re-read the conversation. Without this the bubble could show an answer but never let
   * you rate it, which quietly starves the feedback loop: a "No" is what raises a KB-gap ticket
   * and gets a human to write the missing answer.
   */
  /* The conversation list. Only fetched while the panel is open - a bubble on every page
   * must not poll for chats nobody is looking at. */
  const chatsQuery = useQuery({
    queryKey: ['assistant-chats'],
    queryFn: () => assistantApi.chats(),
    enabled: enabled && open,
    retry: false,
  });
  const chats = chatsQuery.data?.data || [];

  const loadTranscript = async (id) => {
    const res = await assistantApi.chatMessages(id);
    setTranscript(toTranscript(res.data));
  };

  const rate = useMutation({
    mutationFn: ({ messageId, body }) => assistantApi.rate(messageId, body),
    onSuccess: (_res, vars) => {
      setTranscript((t) => t.map((m) => (m.id === vars.messageId
        ? { ...m, resolved: vars.body.resolved, rating: vars.body.rating ?? m.rating } : m)));
    },
  });

  const send = useMutation({
    mutationFn: ({ message, images }) => assistantApi.chat({ chatId: chatId || undefined, message, images }),
    onSuccess: (res, vars) => {
      const d = res.data;
      setChatId(d.chatId);
      setProposal(d.proposal || null);
      setCopy(d.copy || null);
      // Remember it before anything else can fail - this is what survives the refresh.
      if (storeKey) {
        try { localStorage.setItem(storeKey, String(d.chatId)); } catch (_) { /* private mode */ }
      }
      // Optimistic first so the answer appears with no round trip, then re-read for the ids the
      // rating controls need (and any human reply that landed meanwhile).
      setTranscript((t) => [...t, { role: 'user', content: vars.message, images: vars.images },
                                  { role: 'assistant', content: d.reply }]);
      queryClient.invalidateQueries({ queryKey: ['assistant-chats'] });
      loadTranscript(d.chatId).catch(() => { /* keep the optimistic view; rating just waits */ });
    },
    onError: (e, vars) => {
      // Same rule as the full page: a failed send must not cost the customer their typing.
      setDraft((cur) => (cur ? cur : vars.message));
      setImages((cur) => cur.length ? cur : vars.images || []);
      setTranscript((t) => [...t, {
        role: 'error',
        content: e.response?.data?.error || e.message || 'Could not reach the assistant.',
      }]);
    },
  });

  /*
   * Reconnect to the conversation this browser was last using. Runs once the assistant reports
   * enabled, and only while we have no chat of our own, so it can never clobber a live exchange.
   * A saved id that no longer loads (deleted server-side, different account) is dropped rather
   * than retried, so a stale key cannot wedge the bubble shut on every page.
   */
  useEffect(() => {
    if (!enabled || chatId !== null || !storeKey) return undefined;
    let saved = null;
    try { saved = localStorage.getItem(storeKey); } catch (_) { /* private mode */ }
    if (!saved) return undefined;

    let cancelled = false;
    assistantApi.chatMessages(Number(saved))
      .then((res) => {
        if (cancelled) return;
        setChatId(Number(saved));
        setTranscript(toTranscript(res.data));
      })
      .catch(() => {
        if (cancelled) return;
        try { localStorage.removeItem(storeKey); } catch (_) { /* private mode */ }
      });
    return () => { cancelled = true; };
  }, [enabled, storeKey, chatId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript, send.isPending]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Escape closes the panel - expected of anything that floats over the page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // One step at a time: full screen -> corner -> closed.
      if (listOpen) setListOpen(false);
      else if (expanded) minimizeRef.current?.();
      else setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, expanded, listOpen]);

  /*
   * The flag going out from under an open conversation.
   *
   * /assistant/info is refetched (the embedded page polls the same query key every 60s), so an
   * admin turning UIAPI_ASSISTANT off flips `enabled` to false in a browser that is mid-sentence.
   * Until this, that unmounted the whole bubble - panel, overlay, half-typed question and all,
   * with no explanation whatsoever, which reads as a crash rather than as a decision somebody
   * made. Log the transition, because on a customer machine this is the only trace of it.
   */
  const wasEnabled = useRef(false);
  useEffect(() => {
    if (wasEnabled.current && !enabled) {
      console.warn('[assistant] /assistant/info now reports disabled; the assistant was switched'
        + ' off for this account while the bubble was mounted.');
    }
    wasEnabled.current = enabled;
  }, [enabled]);

  // Hidden when it is off AND nobody is looking at it. An OPEN panel stays up and says so
  // instead of vanishing; closing it is what makes the bubble go away.
  if (!enabled && !open) return null;

  /*
   * Switch conversations. Same storeKey the page uses, so whichever surface you pick one in,
   * the other reopens it.
   *
   * LOAD FIRST, then commit. Setting chatId and the saved key before the fetch resolves meant a
   * failed load left chat A's messages on screen and in state under chat B's id - and, worse, in
   * localStorage, so the next reload restored the wrong pairing too. The next successful send
   * happened to heal it, which is exactly why it was invisible. AssistantPage.openChat has always
   * done it in this order; this is the same order, for the same reason.
   */
  const openChat = async (id) => {
    setListOpen(false);
    try {
      await loadTranscript(id);
      setChatId(id);
      // Cleared only once the switch has actually happened: a load that throws leaves the customer
      // on the conversation they were already reading, and its offer is still theirs to accept.
      setProposal(null);
      setCopy(null);
      if (storeKey) {
        try { localStorage.setItem(storeKey, String(id)); } catch (_) { /* private mode */ }
      }
    } catch (e) {
      // No toast - the bubble has nowhere good to put one - but this must not be silent either:
      // the customer pressed a conversation and stayed on the old one, and the only way anyone
      // ever diagnoses that on a machine we cannot reach is this line.
      console.warn(`[assistant] could not open conversation ${id}, staying on ${chatId}:`,
        e?.response?.status || '', e?.message || e);
    }
  };

  /* Start fresh. The saved id is REMOVED, not just unset: leaving it behind means the next
   * reload silently reopens the old conversation the customer just walked away from. */
  const newChat = () => {
    setListOpen(false);
    setProposal(null);
    setCopy(null);
    setChatId(null);
    setTranscript([]);
    if (storeKey) {
      try { localStorage.removeItem(storeKey); } catch (_) { /* private mode */ }
    }
  };

  /* Back to the corner. The embedded page owns its own chat state while expanded, so the
   * bubble re-reads the saved conversation on the way down - otherwise minimising after
   * switching chats would show the transcript you were looking at BEFORE you expanded.
   *
   * The cards go with it. They belong to ONE answer, and while expanded the customer may have
   * asked three more questions or moved to another conversation entirely; keeping them meant a
   * live Apply button under an answer that was no longer on screen. Re-applying is idempotent
   * server-side, so the danger is not a double write - it is the card then reporting "Applied 3
   * settings" for a change the customer no longer has any way to connect to a question they
   * asked. The next reply brings its own offer back if the server still has one. */
  const minimize = () => {
    setExpanded(false);
    setProposal(null);
    setCopy(null);
    let saved = null;
    try { saved = storeKey ? localStorage.getItem(storeKey) : null; } catch (_) { /* private mode */ }
    if (saved) {
      setChatId(Number(saved));
      loadTranscript(Number(saved)).catch((e) => {
        // Leave whatever is on screen, but say so: the corner panel is now showing a transcript
        // that may predate what the expanded view was reading.
        console.warn(`[assistant] could not reload conversation ${saved} on minimize:`,
          e?.response?.status || '', e?.message || e);
      });
    } else {
      setChatId(null);
      setTranscript([]);
    }
  };
  // A plain assignment, not an effect: everything below here sits after the `!enabled && !open`
  // early return, and a hook there would be called conditionally.
  minimizeRef.current = minimize;


  const submit = (e) => {
    if (e) e.preventDefault();
    const message = draft.trim();
    // `enabled` is checked here as well as on the button: Enter in the textarea reaches this
    // directly, and the panel now stays up after the flag is switched off.
    if ((!message && !images.length) || send.isPending || !enabled) return;
    setDraft('');
    setImages([]);
    send.mutate({ message, images });
  };

  // Sit above the mobile tab bar and the home indicator, then above anything holding the corner.
  const bottom = `calc(var(--bottombar-h) + var(--safe-bottom) + 1rem + ${lift}px)`;

  return (
    <>
      {open && expanded && (
        <>
          {/* Click-outside minimises rather than closes - the conversation is not something
            * to throw away by missing the panel. */}
          <div
            className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm"
            onClick={minimize}
            aria-hidden
          />
          <div
            /*
             * Sized up 20% from 1080x880. Each axis is min(fixed, viewport-relative): the pixel
             * cap is what binds on a desktop, the viewport term is what keeps a margin on smaller
             * screens so this still reads as a panel over your page rather than a takeover.
             * Height stays at 92vh on purpose - the viewport, not the cap, is the binding term
             * there on a normal laptop, and chasing a full 20% would put it flush against the
             * edges, which is the full-screen look this was moved away from.
             */
            className="fixed left-1/2 top-1/2 z-50 flex h-[min(92vh,1056px)] w-[min(1296px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl"
            role="dialog"
            aria-label="Undead Assistant"
            data-testid="assistant-bubble-expanded"
          >
            <div className="absolute right-3 top-3 z-10 flex items-center gap-1">
              <button
                type="button"
                onClick={minimize}
                className="tap rounded-md bg-background/70 p-1.5 text-muted-foreground hover:text-foreground"
                title="Shrink to the corner"
                aria-label="Shrink to the corner"
                data-testid="assistant-bubble-minimize"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => { setExpanded(false); setOpen(false); }}
                className="tap rounded-md bg-background/70 p-1.5 text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* The dedicated page itself, not a second implementation of it. */}
            <div className="min-h-0 flex-1 overflow-hidden p-4 sm:p-5">
              <AssistantPage embedded />
            </div>
          </div>
        </>
      )}

      {open && !expanded && (
        <div
          // Corner panel only - the expanded overlay is the block above, with its own sizing.
          // This used to carry `expanded ? ... : ...` in both the class list and the style, left
          // behind when the two views were split; inside `open && !expanded` neither branch could
          // ever be taken.
          className="fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl right-4 lg:right-6 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(70vh,520px)]"
          style={{ bottom: `calc(${bottom} + 3.75rem)` }}
          role="dialog"
          aria-label="Undead Assistant"
          data-testid="assistant-bubble-panel"
        >
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Skull className="h-4 w-4 text-accent" aria-hidden />
            <span className="font-heading text-sm font-semibold text-foreground">
              Undead Assistant
            </span>
            {/* Persistent, not just on the empty state: someone twenty messages deep should
              * still be able to see that this is alpha without scrolling back. */}
            <span className="rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5
              text-[10px] font-semibold uppercase tracking-wide text-warning">
              Alpha
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setListOpen((v) => !v)}
              className="tap rounded-md p-1.5 text-muted-foreground hover:text-foreground"
              title="Conversations"
              aria-label="Conversations"
              aria-pressed={listOpen}
              data-testid="assistant-bubble-conversations"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
            {/* Only ever the EXPAND direction: this header belongs to the corner panel, and the
              * expanded overlay has its own shrink and close buttons. It used to toggle, with
              * `expanded ?` on the icon, the title and the label - all three unreachable. */}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="tap rounded-md p-1.5 text-muted-foreground hover:text-foreground"
              title="Expand to fill the screen"
              aria-label="Expand to fill the screen"
              aria-expanded={false}
              data-testid="assistant-bubble-expand"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="tap rounded-md p-1.5 text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* The flag was switched off under an open conversation. Say so where the answers are,
            * and leave the transcript readable - it is the customer's own question and reply, and
            * it is still saved server-side. The expanded overlay needs no equivalent: it renders
            * AssistantPage, which carries this banner already. */}
          {!enabled && (
            <div className="border-b border-warning/30 bg-warning/10 px-4 py-2 text-xs text-warning"
              role="alert" data-testid="assistant-bubble-disabled">
              The assistant has just been switched off for this account. This conversation is
              saved; you can close this panel.
            </div>
          )}

          {listOpen ? (
            <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
              {/* Frozen while an answer is in flight, exactly like the conversation rows beside
                * it: the reply's onSuccess writes chatId and the transcript unconditionally, so
                * starting a new conversation mid-send lands the old answer in the empty one. */}
              <button
                type="button"
                onClick={newChat}
                disabled={send.isPending}
                className="tap flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground hover:bg-secondary/70 disabled:opacity-50"
                data-testid="assistant-bubble-new-chat"
              >
                <Plus className="h-4 w-4" /> New conversation
              </button>
              <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                {chats.length === 0 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">No conversations yet.</p>
                )}
                {chats.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => openChat(c.id)}
                    disabled={send.isPending}
                    className={[
                      'tap flex w-full min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors disabled:opacity-50',
                      c.id === chatId
                        ? 'bg-primary/15 text-primary ring-1 ring-primary/25'
                        : 'text-foreground/90 hover:bg-secondary/70',
                    ].join(' ')}
                    data-testid={`assistant-bubble-chat-${c.id}`}
                  >
                    {c.unreadSupportAnswer && (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-success" title="A human answered" />
                    )}
                    <span className="min-w-0 truncate">{c.title || `conversation ${c.id}`}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setListOpen(false)}
                className="tap flex items-center gap-1 self-start rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> back to the conversation
              </button>
            </div>
          ) : (
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4 text-sm">
            {transcript.length === 0 && !send.isPending && (
              <p className="text-muted-foreground text-pretty">
                Ask about a setting, an error you are seeing, or why a slot is not running.
                Answers come from this product's own documentation.
              </p>
            )}
            {transcript.length === 0 && !send.isPending && (
              <p className="rounded-lg border border-warning/30 bg-warning/5 p-2.5 text-xs text-muted-foreground text-pretty">
                <span className="font-semibold text-warning">Alpha.</span> This assistant is still
                being built, so it will get things wrong. Check anything it tells you before acting
                on it, and use <span className="font-medium text-foreground">Report to support</span>
                if an answer looks off - that is what improves it.
              </p>
            )}
            {transcript.map((m, i) => (
              <div
                /* `m.id || i` collided: an optimistic row has no id and fell back to its index,
                 * which is a plain number, so a server id that happened to equal a transcript
                 * index gave two rows the same key and React reconciled them into one. The
                 * prefix puts the two spaces apart for good - the page has always used it. */
                key={m.id ?? `tmp-${i}`}
                className={m.role === 'user' ? 'flex justify-end' : 'flex flex-col items-start'}
              >
                <div
                  className={[
                    'max-w-[85%] break-words rounded-2xl px-3 py-2',
                    m.role === 'user' ? 'bg-primary/15 text-foreground'
                      : m.role === 'error' ? 'bg-destructive/15 text-destructive'
                      : 'bg-secondary/60 text-foreground',
                  ].join(' ')}
                >
                  {m.role === 'user' ? <div className="whitespace-pre-wrap break-words">{m.content}</div> : <RichText text={m.content} />}
                  <AssistantAttachments images={m.images} />
                </div>
                {ratingEnabled && m.role === 'assistant' && m.id && (
                  <RatingRow
                    message={m}
                    busy={rate.isPending}
                    onRate={(messageId, body) => rate.mutate({ messageId, body })}
                  />
                )}
              </div>
            ))}
            {proposal && !send.isPending && (
              <ProposalCard
                proposal={proposal}
                onDone={() => chatId && loadTranscript(chatId).catch(() => {})}
                onDismiss={() => setProposal(null)}
              />
            )}
            {copy && !send.isPending && (
              <CopyProposalCard
                copy={copy}
                onDone={() => chatId && loadTranscript(chatId).catch(() => {})}
                onDismiss={() => setCopy(null)}
              />
            )}
            {send.isPending && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                thinking...
              </div>
            )}
          </div>

          )}

<AssistantComposer value={draft} onChange={setDraft} images={images} onImagesChange={setImages} onSend={submit} disabled={!enabled} busy={send.isPending} inputRef={inputRef} testId="assistant-bubble-input" />
        </div>
      )}

      {/* Hidden while full screen: the panel covers the corner, so a floating button on top of
        * it is just something to fumble past. The header keeps shrink and close. */}
      {!(open && expanded) && (
      <div className="fixed right-4 z-50 lg:right-6" style={{ bottom }}>
        {/*
          * [&_svg]:!size-7 is doing real work, not decoration. The Button base class carries
          * `[&_svg]:size-4`, which compiles to a DESCENDANT selector (.btn svg) and therefore
          * outranks any h-6/w-6 put on the icon element itself - so the icon silently rendered
          * at 16px inside a 56px circle no matter what was asked for. The variant here has the
          * same specificity, so `!` is what actually settles it rather than stylesheet order.
          * 28px in a 56px button is half the face, which is what a support FAB wants: a skull
          * has far more internal detail than a chat bubble and reads smaller at the same size.
          */}
        <Button
          onClick={() => setOpen((v) => !v)}
          size="icon"
          className="tap h-14 w-14 rounded-full shadow-glow [&_svg]:!size-7"
          aria-label={open ? 'Close the assistant' : 'Ask the Undead Assistant'}
          aria-expanded={open}
          title="Undead Assistant"
          data-testid="assistant-bubble-button"
        >
          {open ? <X /> : <Skull />}
        </Button>
      </div>
      )}
    </>
  );
}
